import api from "utils/axios";
import * as XLSX from 'xlsx';

export async function getInfluencers(page = 1, limit = 10, searchTerm = "") {
  try {
    const { data } = await api.get(`${process.env.REACT_APP_BASE_URL}/contest/get-influencers`, {
      params: { page, limit, searchTerm },
    });
    if (!data || data.success !== true || !data.data) {
      throw new Error(data.message || "Influencers retrieval failed");
    }
    console.log("Fetched Influencers data:", data.data);
    return {
      influencers: data.data.influencers || [],
      pagination: data.data.pagination || null,
    };
  } catch (error) {
    console.error("getInfluencers error:", error);
    return { influencers: [], pagination: null };
  }
}

export async function deleteInfluencer(id) {
  try {
    const { data } = await api.delete(`${process.env.REACT_APP_BASE_URL}/contest/delete/${id}`);
    if (!data || data.success !== true) {
      throw new Error(data.message || "Failed to delete influencer");
    }
    return data;
  } catch (error) {
    console.error("deleteInfluencer error:", error);
    throw new Error("Failed to delete influencer");
  }
}

export async function updateInfluencer(id, updateData) {
  try {
    const { data } = await api.put(
      `${process.env.REACT_APP_BASE_URL}/contest/update/${id}`,
      updateData
    );
    if (!data || data.success !== true) {
      throw new Error(data.message || "Failed to update influencer");
    }
    return data;
  } catch (error) {
    console.error("updateInfluencer error:", error.response?.data?.message || error);
    throw new Error(error.response?.data?.message || "Failed to update influencer");
  }
}

// Dummy method for getting influencer by ID
export async function getInfluencerById(id) {
  try {
    const { data } = await api.get(
      `${process.env.REACT_APP_BASE_URL}/contest/get-influencer/${id}`
    );
    if (!data || data.success !== true) {
      throw new Error(data.message || "Failed to get influencer details");
    }
    return data.data;
  } catch (error) {
    console.error("getInfluencerById error:", error);
    throw error;
  }
}

// Dummy method for creating new influencer
export async function createInfluencer(influencerData) {
  try {
    const { data } = await api.post(
      `${process.env.REACT_APP_BASE_URL}/contest/create`,
      influencerData
    );
    if (!data || data.success !== true) {
      throw new Error(data.message || "Failed to create influencer");
    }
    return data;
  } catch (error) {
    console.error("createInfluencer error:", error);
    throw error;
  }
}

// Dummy method for bulk influencer operations
export async function bulkInfluencerOperation(operation, influencerIds) {
  try {
    const { data } = await api.post(`${process.env.REACT_APP_BASE_URL}/contest/bulk-operation`, {
      operation,
      influencerIds,
    });
    if (!data || data.success !== true) {
      throw new Error(data.message || "Failed to perform bulk operation");
    }
    return data;
  } catch (error) {
    console.error("bulkInfluencerOperation error:", error);
    throw error;
  }
}

// Dummy method for getting contest statistics
export async function getContestStats() {
  try {
    const { data } = await api.get(`${process.env.REACT_APP_BASE_URL}/contest/stats`);
    if (!data || data.success !== true) {
      throw new Error(data.message || "Failed to get contest statistics");
    }
    return data.data;
  } catch (error) {
    console.error("getContestStats error:", error);
    throw error;
  }
}

// Export influencers data to Excel
export async function exportInfluencersToExcel() {
  try {
    // Fetch all influencers data (without pagination)
    const { data } = await api.get(`${process.env.REACT_APP_BASE_URL}/contest/get-influencers`, {
      params: { page: 1, limit: 10000 }, // Large limit to get all data
    });

    if (!data || data.success !== true || !data.data) {
      throw new Error(data.message || "Failed to fetch influencers data for export");
    }

    const influencers = data.data.influencers || [];

    // Prepare data for Excel export
    const excelData = influencers.map((influencer, index) => {
      const downloads = influencer.downloadCounts || 0;
      const influencerPoints = influencer.influencerPoints || 0;
      const totalPoints = downloads + influencerPoints;

      return {
        'S.No': index + 1,
        'Name': influencer.name || 'N/A',
        'Referral Code': influencer.referralCode || 'N/A',
        'Downloads': downloads,
        'Influencer Points': influencerPoints,
        'Total Points': totalPoints,
        'Video URLs': Array.isArray(influencer.videoUrl) ? influencer.videoUrl.join(', ') : (influencer.videoUrl || 'N/A'),
        'Created At': influencer.createdAt ? new Date(influencer.createdAt).toLocaleString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }) : 'N/A'
      };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better readability
    const columnWidths = [
      { wch: 8 },   // S.No
      { wch: 20 },  // Name
      { wch: 15 },  // Referral Code
      { wch: 12 },  // Downloads
      { wch: 15 },  // Influencer Points
      { wch: 12 },  // Total Points
      { wch: 50 },  // Video URLs
      { wch: 20 }   // Created At
    ];
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Influencers');

    // Generate filename with current date
    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `Influencers_Contest_${currentDate}.xlsx`;

    // Write and download the file
    XLSX.writeFile(workbook, filename);

    return {
      success: true,
      message: `Excel file "${filename}" has been downloaded successfully`,
      filename: filename,
      totalRecords: influencers.length
    };

  } catch (error) {
    console.error("exportInfluencersToExcel error:", error);
    throw new Error(error.response?.data?.message || error.message || "Failed to export influencers data");
  }
}
